// react
import { useEffect, useMemo } from 'react';
// yup
import * as Yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
import Chip from '@mui/material/Chip';
import InputLabel from '@mui/material/InputLabel';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// components
import { useSnackbar } from 'src/components/snackbar';
import FormProvider from 'src/components/hook-form';
// redux
import { useDispatch, useSelector } from 'src/redux/store';
import { setError } from 'src/redux/slices/error';
import {
  getCampaigns,
  getContingents,
  getDropIns,
  getPersonalTrainings,
  getPunchCards,
} from 'src/redux/slices/membership-type';
// localization
import { useLocales } from '../../../locales';
// api
import { WaitingListService, AccountDto } from '../../../api';

type Props = {
  openState: ReturnType<typeof useBoolean>;
  member: AccountDto;
  membershipTypeId: number;
  waitingListId: number;
};

export function MembershipWaitingListDialog({
  openState,
  member,
  membershipTypeId,
  waitingListId,
}: Props) {
  const currentChain = useSelector((state) => state.chain.currentChain);
  const currentGym = useSelector((state) => state.gym.currentGym);

  const campaigns = useSelector((state) => state.membershipType.campaigns);
  const contingents = useSelector((state) => state.membershipType.contingents);
  const punchCards = useSelector((state) => state.membershipType.punchCards);
  const dropIns = useSelector((state) => state.membershipType.dropIns);
  const personalTrainings = useSelector((state) => state.membershipType.personalTrainings);

  const dispatch = useDispatch();

  useEffect(() => {
    if (campaigns.length === 0) {
      dispatch(
        getCampaigns({
          pageSize: 2 ** 31 - 1,
          pageNumber: 0,
          validOn: new Date(),
          sortBy: ['name'],
        })
      );
    }
  }, [dispatch, campaigns]);
  useEffect(() => {
    if (contingents.length === 0) {
      dispatch(
        getContingents({
          pageSize: 2 ** 31 - 1,
          pageNumber: 0,
          validOn: new Date(),
          sortBy: ['name'],
        })
      );
    }
  }, [dispatch, contingents]);
  useEffect(() => {
    if (dropIns.length === 0) {
      dispatch(
        getDropIns({
          pageSize: 2 ** 31 - 1,
          pageNumber: 0,
          validOn: new Date(),
          sortBy: ['name'],
        })
      );
    }
  }, [dispatch, dropIns]);
  useEffect(() => {
    if (personalTrainings.length === 0) {
      dispatch(
        getPersonalTrainings({
          pageSize: 2 ** 31 - 1,
          pageNumber: 0,
          validOn: new Date(),
          sortBy: ['name'],
        })
      );
    }
  }, [dispatch, personalTrainings]);
  useEffect(() => {
    if (punchCards.length === 0) {
      dispatch(
        getPunchCards({
          pageSize: 2 ** 31 - 1,
          pageNumber: 0,
          isActive: true,
          validOn: new Date(),
          sortBy: ['name'],
        })
      );
    }
  }, [dispatch, punchCards]);

  const { t } = useLocales();

  const { enqueueSnackbar } = useSnackbar();

  const newSchema = Yup.object().shape({
    membershipTypeId: Yup.number()
      .min(1, 'Membership is required')
      .required('Membership is required'),
  });

  const defaultValues = useMemo(
    () =>
      ({
        membershipTypeId,
      } as any),
    [membershipTypeId]
  );

  const methods = useForm({
    resolver: yupResolver(newSchema),
    defaultValues,
  });

  const {
    watch,
    handleSubmit,
    formState: { isSubmitting },
    control,
    reset,
    setValue,
  } = methods;

  const values = watch();

  useEffect(() => {
    if (membershipTypeId) {
      reset(defaultValues);
    }
  }, [membershipTypeId, reset, defaultValues, openState]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      await WaitingListService.update({
        id: waitingListId,
        body: { gymId: currentGym.id, chainId: currentChain.id, ...data },
      });
    } catch (error) {
      dispatch(setError(error));
      enqueueSnackbar(t('Error while saving changes!'), { variant: 'error' });
      return;
    }
    reset();
    openState.onFalse();
    enqueueSnackbar(t('Sucessfully saved changes!'));
  });

  const handleMembershipChange = (event: SelectChangeEvent<HTMLInputElement>) => {
    const result = parseInt(`${event.target.value}`, 10);
    setValue('membershipTypeId', result, { shouldValidate: true });
  };

  return (
    <Dialog open={openState.value} onClose={openState.onFalse}>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle>{t('Edit waiting list entry')}</DialogTitle>

        <DialogContent>
          <Typography>
            {t('Please select a diffrent membership type to change the waiting list entry.')}
          </Typography>
          <Stack spacing={3}>
            <Box sx={{ pt: 2 }}>
              <Controller
                name="membershipTypeId"
                control={control}
                render={({ fieldState: { error } }) => (
                  <FormControl fullWidth error={!!error}>
                    <InputLabel>{t('Type')}</InputLabel>
                    <Select
                      label={t('Type')}
                      value={values.membershipTypeId}
                      onChange={handleMembershipChange}
                      autoWidth={false}
                    >
                      {campaigns.map((option) => (
                        <MenuItem
                          key={`membership-type-${option.id}`}
                          value={option.id}
                          sx={{
                            justifyContent: 'space-between',
                          }}
                        >
                          {option.name}
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t('Campaign')}
                            color="success"
                            sx={{ ml: 1 }}
                          />
                        </MenuItem>
                      ))}
                      {contingents.map((option) => (
                        <MenuItem
                          key={`membership-type-${option.id}`}
                          value={option.id}
                          sx={{
                            justifyContent: 'space-between',
                          }}
                        >
                          {option.name}
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t('Contingent')}
                            color="info"
                            sx={{ ml: 1 }}
                          />
                        </MenuItem>
                      ))}
                      {personalTrainings.map((option) => (
                        <MenuItem
                          key={`membership-type-${option.id}`}
                          value={option.id}
                          sx={{
                            justifyContent: 'space-between',
                          }}
                        >
                          {option.name}
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t('Personal training')}
                            color="secondary"
                            sx={{ ml: 1 }}
                          />
                        </MenuItem>
                      ))}
                      {punchCards.map((option) => (
                        <MenuItem
                          key={`membership-type-${option.id}`}
                          value={option.id}
                          sx={{
                            justifyContent: 'space-between',
                          }}
                        >
                          {option.name}
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t('Punch card')}
                            color="warning"
                            sx={{ ml: 1 }}
                          />
                        </MenuItem>
                      ))}
                      {dropIns.map((option) => (
                        <MenuItem
                          key={`membership-type-${option.id}`}
                          value={option.id}
                          sx={{
                            justifyContent: 'space-between',
                          }}
                        >
                          {option.name}
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t('Drop In')}
                            color="default"
                            sx={{ ml: 1 }}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                    {error?.message && <FormHelperText>{error?.message}</FormHelperText>}
                  </FormControl>
                )}
              />
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button color="primary" variant="outlined" onClick={openState.onFalse}>
            {t('Cancel')}
          </Button>
          <Button type="submit" color="primary" variant="contained" loading={isSubmitting}>
            {t('Save changes')}
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
